import React, { useState } from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import {
  FilterSelect,
  FilterOption,
  ModelSearchSelect,
  DateFilterSelect,
  ResponsiveTooltip,
} from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';
import {
  SearchInputIc,
  AccountIcon,
  WidgetIc,
  DangerIc,
  GlobalIc,
  ResetFiltersIc,
} from '../../../../components/icons';
import { StatisticsFilters, StatisticsFiltersProps, Model } from '../types';

const getGroupingOptions = (t: (key: string) => string): FilterOption[] => [
  { key: 'all', label: t('statistics.filters.grouping') },
  { key: 'day', label: t('statistics.filters.day') },
  { key: 'week', label: t('statistics.filters.week') },
  { key: 'month', label: t('statistics.filters.month') },
];

const getAccountOptions = (t: (key: string) => string): FilterOption[] => [
  { key: 'all', label: t('statistics.filters.allAccounts') },
  { key: 'account1', label: t('statistics.filters.account1') },
  { key: 'account2', label: t('statistics.filters.account2') },
];

const getCampaignOptions = (t: (key: string) => string): FilterOption[] => [
  { key: 'all', label: t('statistics.filters.allCampaigns') },
  { key: 'campaign1', label: t('statistics.filters.campaign1') },
  { key: 'campaign2', label: t('statistics.filters.campaign2') },
];

const FiltersSection: React.FC<StatisticsFiltersProps> = ({
  filters,
  localSearch,
  availableModels,
  modelsLoading,
  onSearchChange,
  onFilterChange,
  onModelSearchChange,
}) => {
  const { t } = useTranslations();
  
  const hasActiveFilters = filters.search !== '' ||
    filters.dateRange !== 'all' ||
    filters.grouping !== 'day' ||
    filters.account !== 'all' ||
    filters.campaign !== 'all' ||
    filters.models.length > 0 ||
    (filters.customStartDate !== undefined && filters.customStartDate !== null) ||
    (filters.customEndDate !== undefined && filters.customEndDate !== null);

  const handleResetFilters = () => {
    onFilterChange('search', '');
    onFilterChange('dateRange', 'all');
    onFilterChange('grouping', 'day');
    onFilterChange('account', 'all');
    onFilterChange('campaign', 'all');
    onFilterChange('models', []);
    onFilterChange('customStartDate', null);
    onFilterChange('customEndDate', null);
  };

  const handleDateRangeChange = (value: string) => {
    onFilterChange('dateRange', value);
    // Очищаем кастомные даты для всех предустановленных периодов
    if (value !== 'custom') {
      onFilterChange('customStartDate', null);
      onFilterChange('customEndDate', null);
    }
  };

  const handleCustomDateChange = (startDate: string, endDate: string) => {
    onFilterChange('customStartDate', startDate);
    onFilterChange('customEndDate', endDate);
    onFilterChange('dateRange', 'custom');
  };

  return (
    <div className="px-4 pb-6 md:px-6 md:pb-6 flex items-center gap-4 flex-wrap justify-between w-full">
      <div className="flex items-center gap-4 md:flex-wrap md:justify-start max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
        <DateFilterSelect
          placeholder={t('statistics.filters.date')}
          selectedKey={filters.dateRange}
          onSelectionChange={handleDateRangeChange}
          onCustomDateChange={handleCustomDateChange}
          customStartDate={filters.customStartDate || undefined}
          customEndDate={filters.customEndDate || undefined}
          variant="campaign"
          startContent={
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
              <path
                fill="none"
                stroke="#A8A8B0"
                strokeLinecap="round"
                strokeWidth="1.5"
                d="M8 2v3m8-3v3M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
              />
            </svg>
          }
        />

        <FilterSelect
          placeholder={t('statistics.filters.grouping')}
          options={getGroupingOptions(t)}
          selectedKey={filters.grouping}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('grouping', value)}
          startContent={<WidgetIc />}
          autoWidth={true}
          showPlaceholderWithValue={true}
        />

        <ModelSearchSelect
          placeholder={t('statistics.filters.model.placeholder')}
          options={[
            ...availableModels.map(
              (model) =>
                ({
                  key: model.id,
                  avatar: model.avatar,
                  username: model.username,
                  name: model.name,
                  label: `${model.name} (@${model.username})`,
                }) as FilterOption
            ),
          ]}
          selectedKey={filters.models.length > 0 ? filters.models[0] : 'all'}
          onSelectionChange={(value) => {
            const newModels = value === 'all' ? [] : [value];
            onFilterChange('models', newModels);
          }}
          onSearchChange={onModelSearchChange}
          isLoading={modelsLoading}
          startContent={<AccountIcon className="text-neutral-8 stroke-neutral-8" size={20} />}
          variant="campaign"
          autoWidth={true}
          t={t}
        />

        {/* <FilterSelect
          placeholder={t('statistics.filters.campaign')}
          options={getCampaignOptions(t)}
          selectedKey={filters.campaign}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('campaign', value)}
          startContent={
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
              <path fill="none" stroke="#A8A8B0" strokeLinecap="round" strokeWidth="1.5" d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
          }
          autoWidth={true}
        /> */}

        {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}
        {hasActiveFilters && (
          <Tooltip content={t('statistics.filters.resetTooltip')} placement="top">
            <Button
              //@ts-ignore
              size="xs"
              className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
              onPress={handleResetFilters}
            >
              <ResetFiltersIc />
            </Button>
          </Tooltip>
        )}
      </div>

      {/* Info Tags */}
      <div className="flex items-center gap-2">
        <div className="flex items-center bg-transparent border border-auth-strength-none rounded-lg px-2 py-1">
          <div className="w-2 h-2 bg-danger-500 rounded-full mr-2"></div>
          <span className="text-sm text-auth-ink">{t('statistics.tags.subscribers')}</span>
        </div>
        <div className="flex items-center bg-transparent border border-auth-strength-none rounded-lg px-2 py-1">
          <div className="w-2 h-2 bg-primary rounded-full mr-2"></div>
          <span className="text-sm text-auth-ink">{t('statistics.tags.income')}</span>
        </div>
      </div>
    </div>
  );
};

export default FiltersSection;
